import initCarousel from '../../helpers/carousel';
import {
  cartApi,
  getTranslation,
  unitlessBreakpoints,
  updateCart,
} from '../../helpers';

const selectors = {
  carousel: '[data-accessory-carousel]',
  addButton: '[data-accessory-add]',
};

// The carousel sits inside the buy box, which is half the page at md and up, so it
// shows fewer cards than the full-width feeds do. Splide's own gap is used rather
// than a utility on the list, which it would double-count against slide widths.
const carouselOptions = {
  perPage: 2,
  perMove: 1,
  gap: '1rem',
  padding: { left: 0, right: 32 },
  breakpoints: {
    [unitlessBreakpoints.md]: {
      perPage: 3,
      padding: '0',
    },
  },
};

// How long the button holds its "Added!" label before returning to "+ Add".
const labelResetDelay = 2000;

/**
 * The "Add Accessories" carousel in the PDP buy box.
 *
 * Mounts the Splide instance and quick-adds a card's variant straight to the cart.
 * Cards only carry an add button when the product has a single available variant —
 * anything with options links to its own PDP instead, so there is no option to guess.
 *
 * @export
 * @class ProductAccessories
 */
export default class ProductAccessories {
  /**
   * Creates an instance of ProductAccessories.
   *
   * @param {HTMLElement} el The [data-complementary-products] container
   */
  constructor(el) {
    this.el = el;
    this.timers = new Map();

    if (!this.el.querySelector(selectors.carousel)) {
      return;
    }

    initCarousel(this.el, carouselOptions);

    this.addButtons = this.el.querySelectorAll(selectors.addButton);
    this.addButtons.forEach(button => {
      button.addEventListener('click', this._onAdd);
    });
  }

  /**
   * Tear down listeners and any pending label resets. Called when the container is
   * re-rendered, so a timer cannot write a label back onto a detached button.
   */
  destroy() {
    this.addButtons?.forEach(button => {
      button.removeEventListener('click', this._onAdd);
    });

    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
  }

  _onAdd = e => {
    const button = e.currentTarget;
    const { variantId } = button.dataset;

    if (!variantId || button.disabled) {
      return;
    }

    this._setLabel(button, getTranslation('products.adding'), true);

    cartApi
      .add({ items: [{ id: variantId, quantity: 1 }] })
      .then(response => {
        if (response.error) {
          this._setLabel(button, getTranslation('products.error'));
          return;
        }

        this._setLabel(button, getTranslation('products.added'));

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
        this._setLabel(button, getTranslation('products.error'));
      });
  };

  /**
   * Swap a button's label, remembering the original so it can be restored.
   *
   * @param {HTMLButtonElement} button
   * @param {string} label
   * @param {boolean} [disabled] Hold the button disabled instead of scheduling a reset
   */
  _setLabel(button, label, disabled = false) {
    if (!button.dataset.defaultLabel) {
      button.dataset.defaultLabel = button.textContent.trim();
    }

    button.textContent = label;
    button.disabled = disabled;

    clearTimeout(this.timers.get(button));

    if (disabled) {
      return;
    }

    this.timers.set(
      button,
      setTimeout(() => {
        button.textContent = button.dataset.defaultLabel;
        this.timers.delete(button);
      }, labelResetDelay),
    );
  }
}
